import type { FieldResult, PageResult } from '../shared/desktop-api';

export type LocatedFieldEvidence = {
  evidencePageNumber: number;
  evidenceLineIndexes: number[];
};

const normalizedEvidenceValue = (value: string) =>
  value
    .trim()
    .replace(/[\s\u00a0]+/gu, '')
    .toLocaleLowerCase();

/**
 * Locate a hand-typed value only when the document itself proves one exact source.
 *
 * Typed corrections used to become useful private layout evidence while the saved
 * document and its correction receipt stayed uncited. That made Paige learn
 * without showing the operator what it had learned from. This mirrors the engine's
 * conservative AnchorLines rule, but applies it before the correction is saved so
 * the visible document, durable event, and learner all carry the same evidence.
 *
 * One whole OCR line across the complete document is required. Repeated, partial,
 * fuzzy, or very short values remain uncited rather than being attached to a guess.
 */
export const uniqueTypedFieldEvidence = (
  pages: readonly PageResult[],
  value: string,
): LocatedFieldEvidence | null => {
  const wanted = normalizedEvidenceValue(value);
  if (wanted.length < 3) return null;

  let located: LocatedFieldEvidence | null = null;
  for (const page of pages) {
    const lines =
      page.lines.length > 0
        ? page.lines.map((line, lineIndex) => ({ lineIndex, text: line.text }))
        : [
            ...page.words.reduce((groups, word) => {
              const words = groups.get(word.lineIndex) ?? [];
              words.push(word.text);
              groups.set(word.lineIndex, words);
              return groups;
            }, new Map<number, string[]>()),
          ]
            .sort(([left], [right]) => left - right)
            .map(([lineIndex, words]) => ({ lineIndex, text: words.join(' ') }));

    for (const line of lines) {
      if (normalizedEvidenceValue(line.text) !== wanted) continue;
      if (located) return null;
      located = {
        evidencePageNumber: page.number,
        evidenceLineIndexes: [line.lineIndex],
      };
    }
  }
  return located;
};

/**
 * What a field's citations are worth once an operator has replaced its value.
 *
 * A lookup-filled field carries where the value came from — the rule, the table, the
 * row, and the reading it was matched on — and review prints that under the box. An
 * operator who types `Ashgrove Foundry` over a looked-up `Zeldenrust Castings` was
 * shown their own typing under "matched exactly · row 1 · from VN-0300", and the
 * citation then survived every later re-extraction, because the whole verified record
 * is restored. The engine already states the rule for itself where a rejected value is
 * emptied: a citation for a value the field no longer holds is worse than none,
 * because it describes work that was undone. The correction path was the one place
 * that did not obey it.
 *
 * A barcode is the same claim made the same way — the icon beside the box says the
 * value was read off a symbol on the page — so hand-typed text stops making it. So is
 * a checkbox reading's marked box: it shows which box chose the value it held.
 *
 * Confirming a value is not replacing it. A looked-up value an operator agrees with
 * still came from the table it says it came from, so both citations stand.
 */
export const retainedFieldEvidence = (
  previous: FieldResult | undefined,
  value: string,
): Pick<FieldResult, 'lookupEvidence' | 'barcodeEvidence' | 'markEvidence'> =>
  previous?.value === value
    ? {
        lookupEvidence: previous?.lookupEvidence ?? null,
        barcodeEvidence: previous?.barcodeEvidence ?? null,
        markEvidence: previous?.markEvidence ?? null,
      }
    : { lookupEvidence: null, barcodeEvidence: null, markEvidence: null };
